import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { iconStyles } from '../../../pages/ConversationsPage/styles';
import Calendar from '../DateRangeCalendar/DateRangeCalendar';
import dayjs from '../../../utils/dayjsSetup';
import isStartOfMonth from '../../../utils/isStartOfMonth';
import isEndOfMonth from '../../../utils/isEndOfMonth';
import isStartOfQuarter from '../../../utils/isStartOfQuarter';
import isEndOfQuarter from '../../../utils/isEndOfQuarter';
import isStartOfYear from '../../../utils/isStartOfYear';
import isEndOfYear from '../../../utils/isEndOfYear';

interface CustomDateRangePickerProps {
  dateRange: [Dayjs, Dayjs];
  handleChangeDateRange: (
    startOfRange: dayjs.Dayjs,
    endOfRange: dayjs.Dayjs
  ) => void;
  daysInRange: number;
}

function CustomDateRangePicker({
  dateRange,
  handleChangeDateRange,
  daysInRange
}: CustomDateRangePickerProps): React.ReactElement {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const toggleCalendar = (): void => setShowCalendar(!showCalendar);

  const moveDateRange = (direction: 'back' | 'forward'): void => {
    const operation = direction === 'back' ? 'subtract' : 'add';

    if (
      isStartOfYear(dayjs(dateRange[0])) &&
      isEndOfYear(dayjs(dateRange[1]))
    ) {
      handleChangeDateRange(
        dayjs(dateRange[0])[operation](1, 'year').startOf('year'),
        dayjs(dateRange[1])[operation](1, 'year').endOf('year')
      );
    } else if (
      isStartOfQuarter(dayjs(dateRange[0])) &&
      isEndOfQuarter(dayjs(dateRange[1]))
    ) {
      handleChangeDateRange(
        dayjs(dateRange[0])[operation](1, 'quarter').startOf('quarter'),
        dayjs(dateRange[1])[operation](1, 'quarter').endOf('quarter')
      );
    } else if (
      isStartOfMonth(dayjs(dateRange[0])) &&
      isEndOfMonth(dayjs(dateRange[1]))
    ) {
      handleChangeDateRange(
        dayjs(dateRange[0])[operation](1, 'month').startOf('month'),
        dayjs(dateRange[1])[operation](1, 'month').endOf('month')
      );
    } else {
      handleChangeDateRange(
        dayjs(dateRange[0])[operation](daysInRange, 'day').startOf('day'),
        dayjs(dateRange[1])[operation](daysInRange, 'day').endOf('day')
      );
    }
  };

  const moveDateRangeBack = (): void => moveDateRange('back');
  const moveDateRangeForward = (): void => moveDateRange('forward');

  const formatDate = (date: Dayjs): string =>
    dayjs(date).format('DD MMM YYYY').toUpperCase();

  const buttonLabel = (isNext: boolean): string => {
    if (
      isStartOfYear(dayjs(dateRange[0])) &&
      isEndOfYear(dayjs(dateRange[1]))
    ) {
      return `Move date range 1 year ${isNext ? 'forward' : 'back'}`;
    }
    if (
      isStartOfQuarter(dayjs(dateRange[0])) &&
      isEndOfQuarter(dayjs(dateRange[1]))
    ) {
      return `Move date range 1 quarter ${isNext ? 'forward' : 'back'}`;
    }
    if (
      isStartOfMonth(dayjs(dateRange[0])) &&
      isEndOfMonth(dayjs(dateRange[1]))
    ) {
      return `Move date range 1 month ${isNext ? 'forward' : 'back'}`;
    }
    return `Move date range ${daysInRange} days ${isNext ? 'forward' : 'back'}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        data-testid='date-range'
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '@media (max-width: 1200px)': {
            marginTop: 1
          }
        }}
      >
        <IconButton onClick={moveDateRangeBack} aria-label={buttonLabel(false)}>
          <ChevronLeftIcon sx={{ ...iconStyles, cursor: 'pointer' }} />
        </IconButton>
        <IconButton onClick={toggleCalendar} aria-label='Open date picker'>
          <CalendarTodayIcon sx={{ color: '#011723' }} />
        </IconButton>
        <Typography
          variant='body2'
          sx={{ marginY: 1, fontSize: '0.8125rem', marginRight: 1 }}
        >
          {dateRange[0] ? formatDate(dateRange[0]) : 'DD/MM/YYYY'} -{' '}
          {dateRange[1] ? formatDate(dateRange[1]) : 'DD/MM/YYYY'}
        </Typography>
        <IconButton
          onClick={moveDateRangeForward}
          aria-label={buttonLabel(true)}
        >
          <ChevronRightIcon sx={{ ...iconStyles, cursor: 'pointer' }} />
        </IconButton>
      </Box>
      {showCalendar && (
        <Calendar
          dateRange={dateRange}
          handleChangeDateRange={handleChangeDateRange}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
        />
      )}
    </LocalizationProvider>
  );
}

export default CustomDateRangePicker;
